import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check, ChevronDown } from "lucide-react";

/**
 * Generic multi-select combobox with a built-in search input.
 *
 * Props:
 *   - items:         [{value, label, sublabel?}]
 *   - selectedValues: string[]
 *   - onChange:      (nextValues: string[]) => void
 *   - allLabel:      string   — text when nothing is selected (default "All")
 *   - singularLabel: string   — noun for the plural label ("owners" → "3 owners")
 *   - testIdPrefix:  string
 *   - width:         string   — Tailwind width class (default min-w-[160px])
 */
export default function MultiSelectFilter({
  items = [],
  selectedValues = [],
  onChange,
  allLabel = "All",
  singularLabel = "items",
  testIdPrefix = "multiselect",
  width = "min-w-[160px] max-w-[200px]",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      (it.label || "").toLowerCase().includes(s) ||
      (it.sublabel || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const toggle = (value) => {
    const next = selectedSet.has(value)
      ? selectedValues.filter((x) => x !== value)
      : [...selectedValues, value];
    onChange(next);
  };

  const clearAll = () => {
    onChange([]);
    setSearch("");
  };

  const buttonLabel = (() => {
    if (selectedValues.length === 0) return allLabel;
    if (selectedValues.length === 1) {
      const it = items.find((x) => x.value === selectedValues[0]);
      return it?.label || `1 ${singularLabel.replace(/s$/, "")}`;
    }
    return `${selectedValues.length} ${singularLabel}`;
  })();

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        data-testid={`${testIdPrefix}-trigger`}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 border rounded-md text-sm px-3 py-1.5 bg-white ${width} justify-between transition-colors ${
          selectedValues.length > 0
            ? "border-orange-300 text-orange-700 bg-orange-50"
            : "border-slate-200 text-slate-700"
        }`}
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`${testIdPrefix}-panel`}
          className="absolute z-30 mt-1 left-0 w-64 bg-white border border-slate-200 rounded-md shadow-lg p-2"
        >
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              autoFocus
              data-testid={`${testIdPrefix}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${singularLabel}…`}
              className="w-full pl-7 pr-7 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-orange-400"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className="max-h-64 overflow-y-auto -mx-1 px-1">
            {filtered.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-4">No {singularLabel} match "{search}"</div>
            ) : (
              filtered.map((it) => {
                const isSelected = selectedSet.has(it.value);
                return (
                  <button
                    key={it.value}
                    type="button"
                    data-testid={`${testIdPrefix}-option-${it.value}`}
                    onClick={() => toggle(it.value)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left hover:bg-slate-50 ${
                      isSelected ? "bg-orange-50" : ""
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-orange-500 border-orange-500" : "border-slate-300 bg-white"
                      }`}
                    >
                      {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
                    </span>
                    <span className="truncate flex-1 text-slate-800">{it.label}</span>
                    {it.sublabel && (
                      <span className="text-[10px] text-slate-400 flex-shrink-0">{it.sublabel}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {selectedValues.length > 0 && (
            <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-500">{selectedValues.length} selected</span>
              <button
                type="button"
                data-testid={`${testIdPrefix}-clear`}
                onClick={clearAll}
                className="text-orange-600 hover:text-orange-700 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
